<template>
  <main id="maincontent">
    <div class="container-narrow global-component content-wrapper">
      <div class="event-list-page">
        <div class="filters-wrapper">
          <form class="search-bar" @submit.prevent="submitSearch">
            <label class="search-input-wrapper">
              <input type="text" class="style-small" placeholder="Søg i arrangementer" aria-label="Søg i arrangementer" v-model="searchString" />
              <button type="submit" class="search-button" aria-label="Søg">
                <svg class="icon" width="18" height="18" viewBox="0 0 18 18" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.6 18L10.3 11.7C9.8 12.1 9.225 12.4167 8.575 12.65C7.925 12.8833 7.23333 13 6.5 13C4.68333 13 3.14583 12.3708 1.8875 11.1125C0.629167 9.85417 0 8.31667 0 6.5C0 4.68333 0.629167 3.14583 1.8875 1.8875C3.14583 0.629167 4.68333 0 6.5 0C8.31667 0 9.85417 0.629167 11.1125 1.8875C12.3708 3.14583 13 4.68333 13 6.5C13 7.23333 12.8833 7.925 12.65 8.575C12.4167 9.225 12.1 9.8 11.7 10.3L18 16.6L16.6 18ZM6.5 11C7.75 11 8.8125 10.5625 9.6875 9.6875C10.5625 8.8125 11 7.75 11 6.5C11 5.25 10.5625 4.1875 9.6875 3.3125C8.8125 2.4375 7.75 2 6.5 2C5.25 2 4.1875 2.4375 3.3125 3.3125C2.4375 4.1875 2 5.25 2 6.5C2 7.75 2.4375 8.8125 3.3125 9.6875C4.1875 10.5625 5.25 11 6.5 11Z"
                    fill="currentColor" />
                </svg>
              </button>
            </label>
          </form>
        </div>

        <div v-if="hasError" class="event-list-error" role="alert">
          Der opstod en fejl under indlæsning af arrangementer. Prøv venligst igen senere.
        </div>

        <div v-else-if="data === null">
          <div class="spinner"></div>
        </div>

        <div v-else>
          <event-list-results :data="data" @reset-search-result="resetSearch">
          </event-list-results>

          <pagination v-if="totalPages > 1" :totalPages="totalPages" :currentPage="currentPage"
            @update:currentPage="val => currentPage = val" />
        </div>
      </div>
    </div>
  </main>
</template>


<script setup>

import { onMounted, onUnmounted, ref, watch } from "vue";
import EventListResults from "./eventListResults.vue";
import Pagination from "../pagination.vue";

const data = ref(null)
const hasError = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const searchString = ref('')

// Set while restoring state from the URL (back/forward navigation) so the
let isSyncingFromURL = false

const props = defineProps({
  searchUrl: {
    type: String,
    required: true
  },
  pageId: {
    type: String,
    required: true
  }
});

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  const query = params.get('q');

  if (query) {
    searchString.value = query;
  }

  const initialPage = parsePage(params.get('page'));

  if (initialPage !== 1) {
    // Deep-linked to a specific page: let the watcher fetch it (without pushing a duplicate history entry).
    isSyncingFromURL = true;
    currentPage.value = initialPage;
  } else {
    await fetchData();
  }

  window.addEventListener('popstate', syncFromURL);
})

onUnmounted(() => {
  window.removeEventListener('popstate', syncFromURL);
})

const parsePage = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

// Re-sync state from the URL on browser back/forward, without pushing a new history entry (the currentPage watcher skips updateURL while syncing).
const syncFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  searchString.value = params.get('q') || '';

  const targetPage = parsePage(params.get('page'));

  if (currentPage.value !== targetPage) {
    isSyncingFromURL = true;
    currentPage.value = targetPage;
  } else {
    fetchData();
  }
};

const updateURL = () => {
  const currentURL = new URL(window.location.href);

  if (searchString.value) {
    currentURL.searchParams.set('q', searchString.value);
  } else {
    currentURL.searchParams.delete('q');
  }

  if (currentPage.value > 1) {
    currentURL.searchParams.set('page', currentPage.value);
  } else {
    currentURL.searchParams.delete('page');
  }

  window.history.pushState(null, '', currentURL);
};

const submitSearch = async () => {
  if (currentPage.value !== 1) {
    // The watcher will update the URL and fetch once currentPage resets.
    currentPage.value = 1;
  } else {
    updateURL();
    await fetchData();
  }
};

const resetSearch = async () => {
  searchString.value = ''

  if (currentPage.value !== 1) {
    // The watcher will update the URL and fetch once currentPage resets.
    currentPage.value = 1;
  } else {
    updateURL();
    await fetchData();
  }
};

const fetchData = async () => {
  const searchQuery = encodeURIComponent(searchString.value.trim());
  const url = `${props.searchUrl}?pageId=${props.pageId}&q=${searchQuery}&page=${currentPage.value}`;

  hasError.value = false;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    data.value = await response.json();
    totalPages.value = data.value.totalPages;

    // Guard against an out-of-range page (e.g. manually set via the URL): clamp to the last available page and re-fetch the correct results.
    if (currentPage.value > totalPages.value) {
      currentPage.value = Math.max(totalPages.value, 1);
      return;
    }
  } catch (err) {
    console.error('fetchData failed:', err);
    data.value = null;
    hasError.value = true;
  }
};

watch(currentPage, async () => {
  // Skip the history push when the change originated from the URL itself.
  if (isSyncingFromURL) {
    isSyncingFromURL = false;
  } else {
    updateURL();
  }

  await fetchData();

  const filterWrapper = document.querySelector('.filters-wrapper');
  if (filterWrapper) {
    const offsetTop = filterWrapper.getBoundingClientRect().top + window.scrollY - 180;
    window.scrollTo({ top: offsetTop, behavior: 'smooth' });
  }
});

</script>
